import { useState } from 'react';
import SectionHeading from '@/pages/home/components/SectionHeading';

const guides: { id: number; icon: string; title: string; desc: string }[] = [
  {
    id: 1,
    icon: 'ri-eye-line',
    title: '先看来源，再看结论',
    desc: '一条爆料最重要的是它的来源是否可追溯。截图、录屏、匿名帖都只是线索，不等于证据。先确认信息从哪来，再决定要不要相信。',
  },
  {
    id: 2,
    icon: 'ri-time-line',
    title: '给事件一点时间',
    desc: '很多反转都发生在最初的二十四小时之后。把「等官方回应」当成习惯，能帮你避开绝大多数站错队的情况。',
  },
  {
    id: 3,
    icon: 'ri-shield-keyhole-line',
    title: '守住隐私的边界',
    desc: '吃瓜可以，但不传播未经同意的私人影像、住址、联系方式。越界的围观不仅不道德，也可能触碰法律红线。',
  },
];

const faqs: { id: number; q: string; a: string }[] = [
  {
    id: 1,
    q: '本站的内容来自哪里？',
    a: '内容来自网络公开讨论的整理与转述。我们会对信息来源性质作出标注，但无法保证每一条讨论都真实可靠，因此所有条目仅供参考。',
  },
  {
    id: 2,
    q: '为什么条目里的人物都是化名？',
    a: '使用化名是为了避免对真实个人造成不必要的困扰与误伤。在缺乏官方确认的前提下，我们不希望任何讨论被当成对具体人物的定论。',
  },
  {
    id: 3,
    q: '热度是怎么算出来的？',
    a: '热度综合了页面内话题的讨论量、转发量与关注度，是一个相对指标，只用来反映「有多少人在讨论」，并不代表事件的真实性或严重程度。',
  },
  {
    id: 4,
    q: '如果我发现自己被提及了怎么办？',
    a: '若你认为某条内容存在失实或侵权，请通过页面底部的联系方式与我们沟通，我们会第一时间核实并处理。',
  },
];

export default function GuideSection() {
  const [openId, setOpenId] = useState<number | null>(faqs[0]?.id ?? null);

  return (
    <section id="guide" className="w-full bg-background-100 py-14 md:py-20">
      <div className="w-full px-4 md:px-8">
        <SectionHeading
          eyebrow="GUIDE · 吃瓜指南"
          title="理性吃瓜指南 · 做一个有判断力的围观者"
          desc="吃瓜本身没什么问题，问题在于把传闻当成定论。下面这几条原则，或许能让你在信息洪流里少交点学费。"
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-10 md:mb-14">
          {guides.map((guide) => (
            <article key={guide.id} className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6">
              <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 mb-4">
                <i className={`${guide.icon} text-xl`} aria-hidden="true"></i>
              </span>
              <h3 className="font-heading text-lg md:text-xl text-foreground-950 mb-2">{guide.title}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed font-body">{guide.desc}</p>
            </article>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8">
          <div className="lg:col-span-3">
            <h3 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">常见问题</h3>
            <div className="flex flex-col gap-3">
              {faqs.map((faq) => {
                const open = openId === faq.id;
                return (
                  <div key={faq.id} className="rounded-lg bg-background-50 border border-background-200 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setOpenId(open ? null : faq.id)}
                      aria-expanded={open}
                      className="w-full flex items-center justify-between gap-3 px-4 md:px-5 py-4 text-left cursor-pointer"
                    >
                      <span className="font-heading text-base md:text-lg text-foreground-950">{faq.q}</span>
                      <i
                        className={`${open ? 'ri-subtract-line' : 'ri-add-line'} text-primary-600 text-lg shrink-0`}
                        aria-hidden="true"
                      ></i>
                    </button>
                    {open && (
                      <p className="px-4 md:px-5 pb-4 text-sm text-foreground-600 leading-relaxed font-body animate-fadeUp">
                        {faq.a}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <aside className="lg:col-span-2">
            <div className="rounded-lg bg-accent-100 border border-accent-300 p-5 md:p-6 h-full">
              <span className="w-11 h-11 flex items-center justify-center rounded-md bg-accent-500 text-foreground-950 mb-4">
                <i className="ri-alert-line text-xl" aria-hidden="true"></i>
              </span>
              <h3 className="font-heading text-xl md:text-2xl text-accent-900 mb-3">免责声明</h3>
              <p className="text-sm text-accent-900/90 leading-relaxed font-body mb-3">
                本站所有内容均为网络公开讨论的整理与转述，人物名称均为化名，不代表本网立场，也不构成对任何事实的认定。
              </p>
              <p className="text-sm text-accent-900/90 leading-relaxed font-body mb-4">
                如相关内容涉及侵权或失实，请通过下方联系方式与我们沟通，我们将在核实后及时处理。请理性吃瓜，尊重每一个真实的个体。
              </p>
              <a
                href="#latest"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-accent-500 text-foreground-950 text-sm font-label whitespace-nowrap hover:bg-accent-400 transition-colors cursor-pointer"
              >
                <i className="ri-article-line" aria-hidden="true"></i>
                返回最新黑料合集
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}